import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { MarkdownRenderer } from "../../components/docs/markdown/MarkdownRenderer";
import { DocsBreadcrumbs } from "../../components/docs/ui/DocsBreadcrumbs";
import { DocsPageNav } from "../../components/docs/ui/DocsPageNav";
import { DocsTableOfContents } from "../../components/docs/layout/DocsTableOfContents";
import { useTableOfContents } from "../../hooks/docs/useTableOfContents";
import {
  parseFrontmatter,
  Frontmatter,
} from "../../utils/docs/frontmatterParser";
import {
  loadMarkdownContent,
  findPageByPath,
  getContentIndex,
  getAdjacentPages,
  ContentPage,
} from "../../utils/docs/contentLoader";
import { Skeleton } from "../../components/ui/Skeleton";
import { cn } from "../../lib/utils";

export function DocsArticle() {
  const { section, slug } = useParams<{ section?: string; slug?: string }>();
  const navigate = useNavigate();
  const [content, setContent] = useState<string>("");
  const [frontmatter, setFrontmatter] = useState<Frontmatter | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adjacentPages, setAdjacentPages] = useState<{
    prev: ContentPage | null;
    next: ContentPage | null;
  }>({ prev: null, next: null });

  const { toc, activeId } = useTableOfContents(content);

  useEffect(() => {
    async function loadContent() {
      if (!section) {
        navigate("/docs");
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const { getContentIndexAsync } =
          await import("../../utils/docs/contentLoader");
        const index = await getContentIndexAsync();
        const page = findPageByPath(index, section, slug || "index");

        if (!page) {
          setError("Page not found");
          setLoading(false);
          return;
        }

        const markdownContent = await loadMarkdownContent(page.filePath);
        const parsed = parseFrontmatter(markdownContent);
        setContent(parsed.content);
        setFrontmatter(parsed.frontmatter);

        // Load adjacent pages
        const { prev, next } = getAdjacentPages(index, page);
        setAdjacentPages({ prev, next });
      } catch (err) {
        console.error("Failed to load content:", err);
        setError("Failed to load content");
      } finally {
        setLoading(false);
      }
    }

    loadContent();
  }, [section, slug, navigate]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto">
        <Skeleton className="h-8 w-3/4 mb-4" />
        <Skeleton className="h-4 w-full mb-2" />
        <Skeleton className="h-4 w-5/6 mb-8" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error || !frontmatter) {
    return (
      <div className="max-w-4xl mx-auto">
        <h1 className="su-text-page-title mb-4">Error</h1>
        <p className="text-[var(--su-text-secondary)]">
          {error || "Page not found"}
        </p>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{frontmatter.title} | SendUpdates.ai Docs</title>
        {frontmatter.description && (
          <meta name="description" content={frontmatter.description} />
        )}
        <meta property="og:title" content={frontmatter.title} />
        {frontmatter.description && (
          <meta property="og:description" content={frontmatter.description} />
        )}
        <meta property="og:type" content="article" />
      </Helmet>

      <div className="flex gap-8">
        <article className="flex-1 min-w-0 max-w-none">
          <DocsBreadcrumbs />
          <MarkdownRenderer content={content} />
          {/* DocsPageNav temporarily disabled */}
        </article>
        {toc.length > 0 && (
          <DocsTableOfContents toc={toc} activeId={activeId} />
        )}
      </div>
    </>
  );
}
